import type {
  TrainingData,
  SaveTrainingDataPayload,
  EmailTemplate,
  CreateEmailTemplatePayload,
  PastResponse,
  SavePastResponsePayload,
} from '@drahft/api-client';

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as trainingDataService from '@/services/trainingDataService';

// ─── State ──────────────────────────────────────────────────────────────────

interface TrainingDataState {
  data: TrainingData | null;
  templates: EmailTemplate[];
  pastResponses: PastResponse[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastFetched: number | null;
  activeSection: string | null;
}

const initialState: TrainingDataState = {
  data: null,
  templates: [],
  pastResponses: [],
  isLoading: false,
  isSaving: false,
  error: null,
  lastFetched: null,
  activeSection: null,
};

const CACHE_DURATION = 30000;

// ─── Thunks ─────────────────────────────────────────────────────────────────

export const fetchTrainingData = createAsyncThunk(
  'trainingData/fetch',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const force = options?.force ?? false;
    const state = getState() as { trainingData: TrainingDataState };
    const now = Date.now();

    if (
      !force &&
      state.trainingData.data &&
      state.trainingData.lastFetched &&
      now - state.trainingData.lastFetched < CACHE_DURATION
    ) {
      return state.trainingData.data;
    }

    try {
      const response = await trainingDataService.getTrainingData();
      return (response && 'id' in response ? response : null) as TrainingData | null;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch training data');
    }
  },
  {
    condition: (options, { getState }) => {
      const force = options?.force ?? false;
      const state = getState() as { trainingData: TrainingDataState };
      if (state.trainingData.isLoading) return false;
      if (force) return true;
      if (!state.trainingData.lastFetched) return true;
      return Date.now() - state.trainingData.lastFetched >= CACHE_DURATION;
    },
  }
);

export const saveTrainingData = createAsyncThunk(
  'trainingData/save',
  async (payload: SaveTrainingDataPayload, { rejectWithValue }) => {
    try {
      const result = await trainingDataService.saveTrainingData(payload);
      return { ...payload, id: result.id };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to save training data');
    }
  }
);

export const updateTrainingSection = createAsyncThunk(
  'trainingData/updateSection',
  async ({ key, content }: { key: string; content: string }, { rejectWithValue }) => {
    try {
      await trainingDataService.updateTrainingSection(key, content);
      return { key, content };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update section');
    }
  }
);

// ─── Email Templates ────────────────────────────────────────────────────────

export const fetchEmailTemplates = createAsyncThunk(
  'trainingData/fetchTemplates',
  async (_, { rejectWithValue }) => {
    try {
      return await trainingDataService.getEmailTemplates();
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch templates');
    }
  }
);

export const createEmailTemplate = createAsyncThunk(
  'trainingData/createTemplate',
  async (template: CreateEmailTemplatePayload, { rejectWithValue }) => {
    try {
      return await trainingDataService.createEmailTemplate(template);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create template');
    }
  }
);

export const updateEmailTemplate = createAsyncThunk(
  'trainingData/updateTemplate',
  async (
    { id, updates }: { id: string; updates: Partial<CreateEmailTemplatePayload> },
    { rejectWithValue }
  ) => {
    try {
      return await trainingDataService.updateEmailTemplate(id, updates);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update template');
    }
  }
);

export const deleteEmailTemplate = createAsyncThunk(
  'trainingData/deleteTemplate',
  async (id: string, { rejectWithValue }) => {
    try {
      await trainingDataService.deleteEmailTemplate(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete template');
    }
  }
);

// ─── Past Responses ─────────────────────────────────────────────────────────

export const fetchPastResponses = createAsyncThunk(
  'trainingData/fetchResponses',
  async (_, { rejectWithValue }) => {
    try {
      return await trainingDataService.getPastResponses();
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch past responses');
    }
  }
);

export const savePastResponse = createAsyncThunk(
  'trainingData/saveResponse',
  async (response: SavePastResponsePayload, { rejectWithValue }) => {
    try {
      return await trainingDataService.savePastResponse(response);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to save response');
    }
  }
);

export const deletePastResponse = createAsyncThunk(
  'trainingData/deleteResponse',
  async (id: string, { rejectWithValue }) => {
    try {
      await trainingDataService.deletePastResponse(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete response');
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────────────────────

const trainingDataSlice = createSlice({
  name: 'trainingData',
  initialState,
  reducers: {
    setActiveSection: (state, action: PayloadAction<string | null>) => {
      state.activeSection = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch training data
    builder
      .addCase(fetchTrainingData.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTrainingData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchTrainingData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Save training data
    builder
      .addCase(saveTrainingData.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveTrainingData.fulfilled, (state, action) => {
        state.isSaving = false;
        // Optimistic: merge payload into current data
        if (state.data) {
          if (action.payload.content) {
            state.data.content = { ...state.data.content, ...action.payload.content };
          }
          if (action.payload.tonePreferences) {
            state.data.tonePreferences = {
              ...state.data.tonePreferences,
              ...action.payload.tonePreferences,
            };
          }
          if (action.payload.pricingRules) {
            state.data.pricingRules = {
              ...state.data.pricingRules,
              ...action.payload.pricingRules,
            };
          }
          if (action.payload.availabilityLogic) {
            const nextAvailability = action.payload.availabilityLogic;
            state.data.availabilityLogic = {
              ...state.data.availabilityLogic,
              ...nextAvailability,
              meetingSchedule: {
                ...state.data.availabilityLogic?.meetingSchedule,
                ...nextAvailability.meetingSchedule,
                dayHours: {
                  ...state.data.availabilityLogic?.meetingSchedule?.dayHours,
                  ...nextAvailability.meetingSchedule?.dayHours,
                },
              },
              eventSchedule: {
                ...state.data.availabilityLogic?.eventSchedule,
                ...nextAvailability.eventSchedule,
                dayHours: {
                  ...state.data.availabilityLogic?.eventSchedule?.dayHours,
                  ...nextAvailability.eventSchedule?.dayHours,
                },
              },
            };
          }
          state.data.updatedAt = new Date().toISOString();
        }
        state.lastFetched = null; // Force re-fetch on next read
      })
      .addCase(saveTrainingData.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });

    // Update section
    builder
      .addCase(updateTrainingSection.pending, state => {
        state.isSaving = true;
      })
      .addCase(updateTrainingSection.fulfilled, (state, action) => {
        state.isSaving = false;
        if (state.data) {
          (state.data.content as any)[action.payload.key] = action.payload.content;
          state.data.updatedAt = new Date().toISOString();
        }
        state.lastFetched = null;
      })
      .addCase(updateTrainingSection.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });

    // Email templates
    builder
      .addCase(fetchEmailTemplates.fulfilled, (state, action) => {
        state.templates = action.payload;
      })
      .addCase(createEmailTemplate.fulfilled, (state, action) => {
        state.templates.unshift(action.payload);
      })
      .addCase(updateEmailTemplate.fulfilled, (state, action) => {
        const idx = state.templates.findIndex(t => t.id === action.payload.id);
        if (idx !== -1) {
          state.templates[idx] = action.payload;
        }
      })
      .addCase(deleteEmailTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter(t => t.id !== action.payload);
      });

    // Past responses
    builder
      .addCase(fetchPastResponses.fulfilled, (state, action) => {
        state.pastResponses = action.payload;
      })
      .addCase(savePastResponse.fulfilled, (state, action) => {
        state.pastResponses.unshift(action.payload);
      })
      .addCase(deletePastResponse.fulfilled, (state, action) => {
        state.pastResponses = state.pastResponses.filter(r => r.id !== action.payload);
      });
  },
});

export const { setActiveSection, clearError } = trainingDataSlice.actions;
export default trainingDataSlice.reducer;
